import { useState, useEffect } from 'react';
import { useUrl } from 'crossroad';
import {
    MessageSquare,
    Sparkles,
    Heart,
    Brain,
    Users
} from 'lucide-react';
import { useStudyGuideByPassageData } from '@/data/study-guide/study-guide-by-passage-data-context';
import { DiscoveryCard } from './discovery-card';
import { ContextHero } from '../context/ContextHero';
import { Badge } from '@/components/ui/badge';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import personasData from '@/data/personas.json';

// Assets
import leaderHero from '@/assets/heroes/reading_pathways_hero_v2.png';

interface Props {
    passageId: string;
}

export default function PassageLeaderToolkit({ passageId }: Props) {
    const [, setUrl] = useUrl();
    const { data: studyGuide, isLoading } = useStudyGuideByPassageData(passageId);

    const [expandedDiscussion, setExpandedDiscussion] = useState(true);
    const [expandedActivities, setExpandedActivities] = useState(true);
    const [expandedLeaderGuidance, setExpandedLeaderGuidance] = useState(true);
    const [expandedHooks, setExpandedHooks] = useState(true);
    const [expandedPersonas, setExpandedPersonas] = useState<number[]>([]);

    useEffect(() => {
        if (studyGuide?.personaHooks) {
            setExpandedPersonas(studyGuide.personaHooks.map((_, i) => i));
        }
    }, [studyGuide]);

    const togglePersona = (idx: number) => {
        setExpandedPersonas(prev =>
            prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
        );
    };

    if (isLoading) {
        return <div className="p-8 text-center text-muted-foreground">Loading toolkit...</div>;
    }

    if (!studyGuide || !studyGuide.groupResources) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center space-y-4">
                <div className="bg-muted p-4 rounded-full">
                    <Users className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold">Leader Toolkit Not Available</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                        Please generate the Inductive Study first to unlock group facilitator resources.
                    </p>
                </div>
            </div>
        );
    }

    const { groupResources, personaHooks } = studyGuide;

    const getPersonaTitle = (id: string) => {
        return personasData.personas.find(p => p.id === id)?.title || id;
    };

    return (
        <div className="relative space-y-6">
            <ContextHero
                eyebrow="LEADER'S TOOLKIT"
                headline="Group Resources"
                subtitle="Prepare with creative hooks, group activities, and diverse study perspectives to lead others well."
                image={leaderHero}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
            />

            {/* Section 1: Session Architecture */}
            <div className="space-y-6 pt-8">
                <div className="space-y-1 px-4">
                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                        Session Architecture
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                            Hooks & Group Dynamics
                        </span>
                        <div className="h-px w-8 bg-primary/20" />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
                    {groupResources.groupHooks && groupResources.groupHooks.length > 0 && (
                        <DiscoveryCard
                            isExpanded={expandedHooks}
                            onToggle={() => setExpandedHooks(!expandedHooks)}
                            header={
                                <div className="flex items-center gap-3">
                                    <Sparkles className="h-4 w-4 text-amber-500/70" />
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                                        Meeting Hooks
                                    </h3>
                                </div>
                            }
                        >
                            <ul className="space-y-3">
                                {groupResources.groupHooks.map((hook, i) => (
                                    <li key={i} className="text-sm italic text-foreground/80 leading-relaxed pl-4 border-l-2 border-amber-500/20">
                                        {hook}
                                    </li>
                                ))}
                            </ul>
                        </DiscoveryCard>
                    )}

                    <DiscoveryCard
                        isExpanded={expandedActivities}
                        onToggle={() => setExpandedActivities(!expandedActivities)}
                        header={
                            <div className="flex items-center gap-3">
                                <Heart className="h-4 w-4 text-primary/70" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                                    Group Activities
                                </h3>
                            </div>
                        }
                    >
                        <div className="space-y-4">
                            {groupResources.activities?.map((activity, i) => (
                                <div key={i} className="space-y-1 border-l-2 border-primary/20 pl-4 py-1">
                                    <p className="text-sm font-semibold text-foreground/90">{activity.title}</p>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{activity.description}</p>
                                </div>
                            ))}
                        </div>
                    </DiscoveryCard>
                </div>
            </div>

            {/* Section 2: Facilitating the Dialogue */}
            <div className="space-y-6 pt-12 border-t border-border/10">
                <div className="space-y-1 px-4">
                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                        Deepening the Dialogue
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                            Guiding Questions & Expert Insights
                        </span>
                        <div className="h-px w-8 bg-primary/20" />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
                    <DiscoveryCard
                        isExpanded={expandedDiscussion}
                        onToggle={() => setExpandedDiscussion(!expandedDiscussion)}
                        header={
                            <div className="flex items-center gap-3">
                                <MessageSquare className="h-4 w-4 text-primary/70" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                                    Discussion Starters
                                </h3>
                            </div>
                        }
                    >
                        <ul className="space-y-3">
                            {groupResources.discussionQuestions?.map((q, i) => (
                                <li key={i} className="text-sm text-foreground/80 leading-relaxed pl-4 border-l-2 border-primary/30">
                                    {q}
                                </li>
                            ))}
                        </ul>
                    </DiscoveryCard>

                    <DiscoveryCard
                        isExpanded={expandedLeaderGuidance}
                        onToggle={() => setExpandedLeaderGuidance(!expandedLeaderGuidance)}
                        header={
                            <div className="flex items-center gap-3">
                                <Brain className="h-4 w-4 text-amber-500/70" />
                                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                                    Leader Guidance
                                </h3>
                            </div>
                        }
                    >
                        <div className="space-y-4">
                            <div className="space-y-2 border-l-2 border-amber-500/30 pl-4">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Observation Tips</p>
                                <p className="text-sm text-foreground/80 leading-relaxed">{groupResources.leaderGuidance.observationTips}</p>
                            </div>
                            {groupResources.leaderGuidance.markingGuidance && (
                                <div className="space-y-2 border-l-2 border-amber-500/30 pl-4">
                                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Marking Guidance</p>
                                    <p className="text-sm text-foreground/80 leading-relaxed">{groupResources.leaderGuidance.markingGuidance}</p>
                                </div>
                            )}
                            <div className="space-y-2 border-l-2 border-amber-500/30 pl-4">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Themes to Highlight</p>
                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    {groupResources.leaderGuidance.keyThemesToHighlight.map((theme, i) => (
                                        <Badge key={i} variant="secondary" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20">
                                            {theme}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </DiscoveryCard>
                </div>
            </div>

            {/* All Perspectives Section */}
            {personaHooks && personaHooks.length > 0 && (
                <div className="space-y-6 pt-12 px-4 border-t border-border/20">
                    <div className="space-y-1 px-2">
                        <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                            Connecting with Diverse Perspectives
                        </h2>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                Tailored Entry Points
                            </span>
                            <div className="h-px w-8 bg-primary/20" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {personaHooks.map((hook, idx) => (
                            <DiscoveryCard
                                key={idx}
                                isExpanded={expandedPersonas.includes(idx)}
                                onToggle={() => togglePersona(idx)}
                                header={
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="h-3.5 w-3.5 text-primary/50" />
                                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                            {getPersonaTitle(hook.personaId)}
                                        </span>
                                    </div>
                                }
                            >
                                <p className="text-sm italic text-foreground/80 leading-relaxed">
                                    "{hook.hook}"
                                </p>
                            </DiscoveryCard>
                        ))}
                    </div>
                </div>
            )}

            {/* AI Disclaimer */}
            <div className="mt-20 pt-12 border-t border-border/20 flex flex-col items-center gap-6 pb-12">
                <AIDisclaimerBadge
                    model={studyGuide?.generatedBy}
                    passageId={passageId}
                    contextType="study-guide"
                />
            </div>
        </div>
    );
}
